import axios from 'axios';
import { API_URL } from '../environment';

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 60000, // 60 seconds default timeout
  withCredentials: true, // send HttpOnly refresh cookie
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── Token storage (in-memory only) ─────────────────────────────────────────
let _accessToken = null;

export const setAccessToken = (token) => { _accessToken = token; };
export const getAccessToken = () => _accessToken;
export const clearAccessToken = () => { _accessToken = null; };

// Retry function for failed requests
const retryRequest = async (fn, retries = 2, delay = 1000) => {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0 && (
      error.code === 'ECONNABORTED' || 
      error.code === 'NETWORK_ERROR' ||
      (error.response && error.response.status >= 500)
    )) {
      console.log(`Retrying request... ${retries} attempts left`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryRequest(fn, retries - 1, delay * 2);
    }
    throw error;
  }
};

// Request interceptor — attach Bearer token + logging
apiClient.interceptors.request.use(
  (config) => {
    if (_accessToken) {
      config.headers.Authorization = `Bearer ${_accessToken}`;
    }
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// ── Silent refresh on 401 ──────────────────────────────────────────────────
let _isRefreshing = false;
let _failedQueue = [];

const processQueue = (error, token = null) => {
  _failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  _failedQueue = [];
};

// Response interceptor for handling common errors
apiClient.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Try silent refresh on 401 (skip for auth endpoints)
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/')
    ) {
      if (_isRefreshing) {
        return new Promise((resolve, reject) => {
          _failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      _isRefreshing = true;

      try {
        const { data } = await axios.post(
          `${API_URL}/auth/refresh`,
          {},
          { withCredentials: true }
        );
        const newToken = data.access_token;
        setAccessToken(newToken);
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAccessToken();
        // Redirect to login
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        _isRefreshing = false;
      }
    }

    console.error('API Response Error:', error);
    
    // Handle common error scenarios
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      error.message = 'Request timed out. Please try again.';
    } else if (error.response?.status === 404) {
      error.message = 'API endpoint not found.';
    } else if (error.response?.status >= 500) {
      error.message = 'Server error. Please try again later.';
    } else if (!navigator.onLine) {
      error.message = 'No internet connection.';
    }
    
    return Promise.reject(error);
  }
);

// API functions
export const searchProjects = async (query, limit = 15) => {
  return retryRequest(async () => {
    const response = await apiClient.get('/search', {
      params: { query: query.trim(), limit },
      timeout: 90000, // 90 seconds for AI search
    });
    return response.data;
  });
};

export const searchProjectsStructured = async (searchData) => {
  return retryRequest(async () => {
    const response = await apiClient.post('/compounds/search/structured', searchData, {
      timeout: 90000, // 90 seconds for AI search
    });
    return response.data;
  });
};

export const analyzePDFs = async (files) => {
  return retryRequest(async () => {
    const formData = new FormData();
    
    if (Array.isArray(files)) {
      files.forEach(file => formData.append('files', file));
    } else {
      formData.append('files', files);
    }
    
    const response = await apiClient.post('/compounds/analyze-multiple-pdfs', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 5 minutes for PDF processing
    });
    
    return response.data;
  }, 1); // Only 1 retry for file uploads
};

export const analyzeProjectsByName = async (projectNames) => {
  return retryRequest(async () => {
    const response = await apiClient.post('/compounds/analyze-projects-by-name', {
      project_names: projectNames  // Wrap in object with correct key
    }, {
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 300000, // 5 minutes for AI analysis with grounding
    });
    
    return response.data;
  }, 1); // Only 1 retry for AI analysis
};

// Get all property units from database
export const getAllPropertyUnits = async () => {
  return retryRequest(async () => {
    const response = await apiClient.get('/property-units/search', {
      timeout: 30000, // 30 seconds for database query
    });
    return response.data.results || []; // Extract the results array
  });
};

export const checkHealth = async () => {
  const response = await apiClient.get('/health', {
    timeout: 5000, // 5 seconds for health check
  });
  return response.data;
};

// Test API connectivity
export const testConnection = async () => {
  try {
    const start = Date.now();
    const data = await checkHealth();
    const duration = Date.now() - start;
    return {
      success: true,
      data,
      duration,
      message: `Connected successfully in ${duration}ms`
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      message: `Connection failed: ${error.message}`
    };
  }
};

export const getProjectDropdownItems = async () => {
  return retryRequest(async () => {
    const response = await apiClient.get('/project-dropdown');
    return response.data.map(project => ({
      id: project.id,
      name: project.project_name || project.name
    }));
  });
};


// Assign payment plan to a project
// ============================================
// Projects Management API Functions
// ============================================

/**
 * Get all projects with their brochure status
 * @param {number} skip - Number of records to skip
 * @param {number} limit - Maximum number of records to return
 * @returns {Promise<Object>} Projects list with metadata
 */
export const getAllProjects = async (skip = 0, limit = 100) => {
  return retryRequest(async () => {
    const response = await apiClient.get('/projects/', {
      params: { skip, limit },
      timeout: 30000,
    });
    return response.data;
  });
};

/**
 * Get a single project by ID
 * @param {number} projectId - The project ID
 * @returns {Promise<Object>} Project details
 */
export const getProjectById = async (projectId) => {
  return retryRequest(async () => {
    const response = await apiClient.get(`/projects/${projectId}`, {
      timeout: 20000,
    });
    return response.data;
  });
};

/**
 * Search projects by name or location
 * @param {string} query - Search query
 * @param {number} limit - Maximum results
 * @returns {Promise<Object>} Search results
 */
export const searchProjectsByName = async (query, limit = 50) => {
  return retryRequest(async () => {
    const response = await apiClient.get('/projects/search/', {
      params: { q: query, limit },
      timeout: 20000,
    });
    return response.data;
  });
};

/**
 * Upload a brochure to a project
 * @param {number} projectId - The project ID
 * @param {File} file - The PDF file to upload
 * @returns {Promise<Object>} Upload result with brochure URL
 */
export const uploadProjectBrochure = async (projectId, file) => {
  return retryRequest(async () => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await apiClient.post(
      `/projects/${projectId}/brochures`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 2 minutes for file upload
      }
    );
    return response.data;
  });
};

/**
 * Delete a brochure from a project
 * @param {number} projectId - The project ID
 * @param {string} brochureUrl - URL of the brochure to delete
 * @returns {Promise<Object>} Deletion result
 */
export const deleteProjectBrochure = async (projectId, brochureUrl) => {
  return retryRequest(async () => {
    const response = await apiClient.delete(`/projects/${projectId}/brochures`, {
      params: { brochure_url: brochureUrl },
      timeout: 20000,
    });
    return response.data;
  });
};

/**
 * Get total projects count
 * @returns {Promise<Object>} Count result
 */
export const getProjectsCount = async () => {
  return retryRequest(async () => {
    const response = await apiClient.get('/projects/stats/count', {
      timeout: 10000,
    });
    return response.data;
  });
};

// ==================== Property Unit Functions ====================

/**
 * Get property units by project ID
 * @param {number} projectId - The project ID
 * @param {number} limit - Maximum number of results (default 1000)
 * @returns {Promise<Object>} Units data with project info
 */
export const getUnitsByProject = async (projectId, limit = 1000) => {
  return retryRequest(async () => {
    const response = await apiClient.get(`/property-units/project/${projectId}`, {
      params: { limit },
      timeout: 30000,
    });
    return response.data;
  });
};

/**
 * Upload Excel file and process for property units
 * @param {File} file - The Excel file to upload
 * @param {number} projectId - The project ID
 * @returns {Promise<Object>} Processing result
 */
export const uploadExcelFile = async (file, projectId, mode = 'replace') => {
  return retryRequest(async () => {
    const formData = new FormData();
    formData.append('excel_file', file);
    if (projectId !== null && projectId !== undefined) {
      formData.append('project_id', projectId);
    }
    formData.append('mode', mode);
    
    const response = await apiClient.post('/process-excel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 5 minutes for large Excel files
    });
    return response.data;
  });
};

/**
 * Search property units with filters
 * @param {Object} filters - Search filters
 * @returns {Promise<Object>} Search results
 */
export const searchPropertyUnits = async (filters = {}) => {
  return retryRequest(async () => {
    const response = await apiClient.get('/property-units/search', {
      params: filters,
      timeout: 30000,
    });
    return response.data;
  });
};

/**
 * Get property unit statistics
 * @returns {Promise<Object>} Statistics
 */
export const getPropertyStats = async () => {
  return retryRequest(async () => {
    const response = await apiClient.get('/property-units/stats', {
      timeout: 10000,
    });
    return response.data;
  });
};

// ============================================
// Admin Management API Functions
// ============================================

/**
 * Invite a user as broker or admin
 * @param {string} email - Email to invite
 * @param {string} role - "broker" or "admin"
 */
export const inviteAdmin = async (email, role = 'broker') => {
  const response = await apiClient.post('/auth/admin/invite', { email, role });
  return response.data;
};

/**
 * List all users (admin-only)
 * @param {number} skip
 * @param {number} limit
 */
export const listUsers = async (skip = 0, limit = 100) => {
  const response = await apiClient.get('/auth/admin/users', {
    params: { skip, limit },
  });
  return response.data;
};

/**
 * List all invitations (admin-only)
 */
export const listInvitations = async () => {
  const response = await apiClient.get('/auth/admin/invitations');
  return response.data;
};

/**
 * Delete a user by ID (admin-only)
 * @param {string} userId
 */
export const deleteUser = async (userId) => {
  const response = await apiClient.delete(`/auth/admin/users/${userId}`);
  return response.data;
};

/**
 * Update roles for a user (admin-only)
 * @param {string} userId
 * @param {string[]} roles - e.g. ["broker"] or ["admin"]
 */
export const updateUserRoles = async (userId, roles) => {
  const response = await apiClient.put(`/auth/admin/users/${userId}/roles`, { roles });
  return response.data;
};

/**
 * Set password for current user
 * @param {string} newPassword
 */
export const setPasswordForCurrentUser = async (newPassword) => {
  const response = await apiClient.post('/auth/me/set-password', { new_password: newPassword });
  return response.data;
};

/**
 * Request a password reset email for an account that cannot sign in.
 * @param {string} email
 */
export const requestPasswordReset = async (email) => {
  const response = await apiClient.post('/auth/forgot-password', { email });
  return response.data;
};

export default apiClient;

// ── Companies (admin-only) ───────────────────────────────────────────────────
// External companies whose staff maintain inventory for assigned compounds.
// See docs/company-entities-design.md.

/**
 * Create a company. The response carries `registration_code` — this is the ONLY
 * time it is ever returned. It is stored hashed and cannot be retrieved later;
 * losing it means regenerating.
 */
export const createCompany = async ({ name, contact_email, max_brokers }) => {
  const response = await apiClient.post('/auth/admin/companies', {
    name,
    contact_email,
    max_brokers: max_brokers || null,
  });
  return response.data;
};

/** List companies. Never includes registration codes — only `code_last4`. */
export const listCompanies = async () => {
  const response = await apiClient.get('/auth/admin/companies');
  return response.data;
};

/** Rename, change the broker cap, or activate/deactivate. */
export const updateCompany = async (companyId, patch) => {
  const response = await apiClient.patch(`/auth/admin/companies/${companyId}`, patch);
  return response.data;
};

/**
 * Issue a new registration code and invalidate the old one. Returns the new
 * code once. Existing staff accounts are unaffected.
 */
export const regenerateCompanyCode = async (companyId) => {
  const response = await apiClient.post(`/auth/admin/companies/${companyId}/regenerate-code`);
  return response.data;
};

/**
 * Replace the company's compound assignments with `compoundIds`.
 * This is the only thing that grants a company visibility — an empty list
 * leaves them able to see and do nothing.
 */
export const setCompanyCompounds = async (companyId, compoundIds) => {
  const response = await apiClient.put(`/auth/admin/companies/${companyId}/compounds`, {
    compound_ids: compoundIds,
  });
  return response.data;
};

/** Delete a company. Staff accounts survive, detached and scoped to nothing. */
export const deleteCompany = async (companyId) => {
  const response = await apiClient.delete(`/auth/admin/companies/${companyId}`);
  return response.data;
};
